import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
  InputAdornment,
  Tabs,   // Added: for category tab bar
  Tab,     // Added: for individual category tabs
  Drawer,
  Chip,
  CircularProgress,
  Divider,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { uwinAPI } from '../../../services/uwin.api';
import { decryptOpenSSLAES } from "../components/uwin-decrypt";
import Loader from '../../../components/Loader'; 
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';

// Hardcoded category tabs — values must match session_site_category from API
const CATEGORY_TABS = ['Government', 'Private'];

const UIPVaccinationCenter = () => {
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  
  // State management
  const [searchType, setSearchType] = useState('1'); // 1: Session Site, 2: Sub Center
  const [searchText, setSearchText] = useState('');
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Active category tab — defaults to 'Government' on page load
  const [activeCategory, setActiveCategory] = useState('Government');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSessions, setSiteSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSessionTypeFilter, setSelectedSessionTypeFilter] = useState('All');

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterData();
  }, [searchType, rawData, activeCategory]); // Re-filter whenever category tab changes

  // Fetch data from API
  const fetchData = async () => {
    try {
      setIsLoading(true); 
      const response = await uwinAPI.getCCPSitesForHFM();
      console.log('API Response:', response);
      
      // Transform the API response into table data Check if response has data property and it's an array
      if (response && response.data && Array.isArray(response.data)) {
        const transformedData = transformApiData(response.data);
        setRawData(transformedData);
      } 
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false); 
    }
  };

  const transformApiData = (apiData) => {
    const rows = [];
    
    // Ensure apiData is an array and not empty
    if (!Array.isArray(apiData)) {
      return rows;
    }
    
    apiData.forEach((facility) => {
      // Add session sites
      if (facility.session_sites && Array.isArray(facility.session_sites)) {
        facility.session_sites.forEach((site) => {
          rows.push({
            id: decryptOpenSSLAES(site.session_site_id), // Decrypted for display
            encryptedId: site.session_site_id,           // Keep encrypted for API calls
            session_site_id: site.session_site_id,
            name: site.session_site_name,
            type: 'session_site',
            category: site.session_site_category || '',  // Category for tab filtering
            uip_session_type: site.uip_session_type || [], // Session type names for form dropdown
            facilityName: facility?.facility_name,
            vaccinesProvided: site.vaccines_provided || ''
          });
        });
      }

      // Add sub centers
      if (facility.sub_centers && Array.isArray(facility.sub_centers)) {
        facility.sub_centers.forEach((center) => {
          rows.push({
            id: decryptOpenSSLAES(center.sub_center_id), // Decrypted for display
            encryptedId: center.sub_center_id,           // Keep encrypted for API calls
            session_site_id: center.sub_center_id,
            name: center.sub_center_name,
            type: 'sub_center',
            category: center.session_site_category || '', // NEW: map category for tab filtering
            facilityName: facility?.facility_name,
            vaccinesProvided: ''
          });
        });
      }
    });

    return rows;
  };

  const filterData = () => {
    let filtered = [...rawData]; // Create a new array to avoid mutation

    // Step 1: Filter by active category tab (Government / Private / Inactive)
    filtered = filtered.filter(item => item.category === activeCategory);

    // Step 2: Filter by type (Session Site or Sub Center)
    if (searchType === '1') {
      filtered = filtered.filter(item => item.type === 'session_site');
    } else if (searchType === '2') {
      filtered = filtered.filter(item => item.type === 'sub_center');
    }

    // Step 3: Filter by search text only if search text is not empty
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchLower) ||
        item.id?.toLowerCase().includes(searchLower) ||
        item.facilityName?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    filterData();
  };

  const handleClear = () => {
    setSearchText(''); // Clear the search text
    
    // Re-filter respecting active category and current search type
    let filtered = [...rawData];
    
    // Apply category filter
    filtered = filtered.filter(item => item.category === activeCategory);

    // Apply type filter
    if (searchType === '1') {
      filtered = filtered.filter(item => item.type === 'session_site');
    } else if (searchType === '2') {
      filtered = filtered.filter(item => item.type === 'sub_center');
    }
    
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    setSearchText(''); // Clear search text when changing type
    setCurrentPage(1); // Reset to first page
  };

  // Handle category tab change — resets search text and page
  const handleCategoryChange = (event, newCategory) => {
    setActiveCategory(newCategory);
    setSearchText('');   // Clear search when switching category
    setCurrentPage(1);  // Reset pagination
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle create session button click
  const handleCreateSession = (sessionSite) => {
    // Navigate to create vaccination session page with session site data
    navigate('/create-vaccination-session', {
      state: {
        sessionSite: sessionSite
      }
    });
  };

  // Handle row click to open drawer
  const handleRowClick = async (row) => {
    setSelectedSite(row);
    setDrawerOpen(true);
    setSelectedSessionTypeFilter('All');
    setLoadingSessions(true);
    try {
      const response = await uwinAPI.getSessionDetails({ session_site_id: row.session_site_id });
      if (response?.success && Array.isArray(response.data)) {
        setSiteSessions(response.data);
      } else {
        setSiteSessions([]);
      }
    } catch (error) {
      console.error('Error fetching session details:', error);
      setSiteSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const groupSessionsByDate = (sessions) => {
    const grouped = {};
    sessions.forEach(session => {
      const dateKey = dayjs(session.date_of_session).format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = new Set();
      }
      grouped[dateKey].add(session.uip_session_category);
    });
    return Object.keys(grouped).sort().map(date => ({
      date,
      types: Array.from(grouped[date])
    }));
  };

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const currentPageData = getCurrentPageData();

  return (
    <Box sx={{ py: 3 }}>
      {/*  Loader component */}
      {isLoading && <Loader />}
      
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  color: '#183b56'
                }}
              >
                UIP Vaccination Session Sites
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#5a7184',
                  fontSize: '16px'
                }}
              >
                Manage vaccination Session Sites, vaccines and staff at various session sites.
              </Typography>
            </Box>
          </Stack>
        </Box>

       {/* Search Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          {/* Search Type Radio Buttons */}
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography 
                  component="span" 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#183b56'
                  }}
                >
                  Search with:
                </Typography>
                <RadioGroup
                  row
                  value={searchType}
                  onChange={handleSearchTypeChange}
                >
                  <FormControlLabel 
                    value="1" 
                    control={<Radio size="small" />} 
                    label="Vaccination Session Site"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '14px',
                        color: '#5a7184'
                      } 
                    }}
                  />
                  <FormControlLabel 
                    value="2" 
                    control={<Radio size="small" />} 
                    label="Sub center"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '14px',
                        color: '#5a7184'
                      } 
                    }}
                  />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>

          {/* Search Input */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems="stretch"
          >
            <TextField
              fullWidth
              placeholder={
                searchType === '1' 
                  ? "Search UIP Vaccination Session Site..." 
                  : "Search Sub center..."
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#5a7184' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                maxWidth: { sm: '500px' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff'
                }
              }}
              size="small"
            />
            
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!searchText.trim()}
              sx={{
                minWidth: '120px',
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              Search
            </Button>

            {/* Clear Button */}
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={!searchText.trim()}
              sx={{
                minWidth: '120px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#c62828',
                  backgroundColor: '#ffebee'
                },
                '&.Mui-disabled': {
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>

        {/* Category Tabs — Government / Private */}
        <Box sx={{ mb: 0 }}>
          <Tabs
            value={activeCategory}
            onChange={handleCategoryChange}
            sx={{
              borderBottom: '1px solid #e0e0e0',
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: '2px'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                color: '#5a7184',        // Inactive tab — secondary text color
                minWidth: '120px',
                '&.Mui-selected': {
                  color: '#1976d2',      // Active tab — primary blue
                  fontWeight: 600
                }
              }
            }}
          >
            {/* Render one tab per hardcoded category */}
            {CATEGORY_TABS.map((category) => (
              <Tab
                key={category}
                label={category}
                value={category}
              />
            ))}
          </Tabs>
        </Box>

        {/* Table Section */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#183b56' }}>SL No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#183b56' }}>Session Site Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#183b56' }}>Session Site Id</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#183b56' }}>Vaccines Provided</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No data found. Try adjusting your search criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentPageData.map((row, index) => (
                    <TableRow 
                      key={`${row?.type}-${row?.id}-${index}`} // Unique key
                      hover
                      onClick={() => handleRowClick(row)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f9f9f9'
                        }
                      }}
                    >
                      <TableCell sx={{ color: '#5a7184' }}>
                        {/* Calculate proper serial number based on current page */}
                        {(currentPage - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ color: '#183b56' }}>{row?.name}</TableCell>
                      <TableCell sx={{ color: '#5a7184' }}>{row?.id}</TableCell>
                      <TableCell sx={{ color: '#5a7184' }}>
                        {row?.vaccinesProvided}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {/*  Correct pagination text display */}
                Showing {((currentPage - 1) * rowsPerPage) + 1} to{' '}
                {Math.min(currentPage * rowsPerPage, filteredData.length)} of{' '}
                {filteredData.length} entries
              </Typography>
              
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>

        {/* Side Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: { xs: '100%', sm: 450 }, p: 3, display: 'flex', flexDirection: 'column' } }}
        >
          {selectedSite && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ pr: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#183b56' }}>
                    {selectedSite.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSite.id}
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setDrawerOpen(false)} 
                  aria-label="close"
                  sx={{ color: '#5a7184' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {/* Pills for Session Types */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                <Chip
                  label="All Sessions"
                  onClick={() => setSelectedSessionTypeFilter('All')}
                  color={selectedSessionTypeFilter === 'All' ? 'primary' : 'default'}
                  variant={selectedSessionTypeFilter === 'All' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: selectedSessionTypeFilter === 'All' ? 600 : 400 }}
                  clickable
                />
                {selectedSite.uip_session_type && selectedSite.uip_session_type.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    onClick={() => setSelectedSessionTypeFilter(type)}
                    color={selectedSessionTypeFilter === type ? 'primary' : 'default'}
                    variant={selectedSessionTypeFilter === type ? 'filled' : 'outlined'}
                    sx={{ fontWeight: selectedSessionTypeFilter === type ? 600 : 400 }}
                    clickable
                  />
                ))}
              </Box>

              {/* Content Area */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                {loadingSessions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedSessionTypeFilter === 'All' ? (
                  // Listing View
                  siteSessions.length === 0 ? (
                    <Typography color="text.secondary">No sessions scheduled.</Typography>
                  ) : (
                    <Stack spacing={2}>
                      {groupSessionsByDate(siteSessions).map(({ date, types }) => (
                        <Paper key={date} variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#183b56', mb: 1 }}>
                            {dayjs(date).format('MMMM D, YYYY')}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {types.map(type => (
                              <Chip key={type} label={type} size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }} />
                            ))}
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )
                ) : (
                  // Calendar View
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      readOnly
                      slots={{
                        day: (dayProps) => {
                          const { day, outsideCurrentMonth, ...rest } = dayProps;
                          const bookedDates = siteSessions
                            .filter(s => s.uip_session_category === selectedSessionTypeFilter)
                            .map(s => dayjs(s.date_of_session).startOf('day'));
                          const isBooked = bookedDates.some(booked => booked.isSame(day.startOf('day'), 'day'));
                          return (
                            <Box component="div" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <Box
                                component="div"
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isBooked ? '#e3f2fd' : 'transparent',
                                  color: outsideCurrentMonth ? '#bdbdbd' : (isBooked ? '#1565c0' : 'inherit'),
                                  fontWeight: isBooked ? 600 : 400,
                                  position: 'relative'
                                }}
                              >
                                {day.date()}
                                {isBooked && (
                                  <Box
                                    component="span"
                                    sx={{
                                      position: 'absolute',
                                      bottom: 4,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      backgroundColor: '#1976d2',
                                      display: 'block'
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          );
                        }
                      }}
                    />
                  </LocalizationProvider>
                )}
              </Box>

              {/* Action Button */}
              <Button
                variant="contained"
                size="large"
                onClick={() => handleCreateSession(selectedSite)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                Create Session
              </Button>
            </>
          )}
        </Drawer>
      </Container>
    </Box>
  );
};

export default UIPVaccinationCenter;